import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageSquare, Calendar, CheckSquare, ClipboardList, Zap, Mail, Reply, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { leadsAPI, nachfassAPI, autopflegeAPI } from '@/lib/api';

/* Kanal-Zeitstrahl (v5, M3): alles zu einem Lead in einer Zeitreihe —
   Nachrichten aller Kanaele, Termine, Ereignisse, Aufgaben, Freigaben.
   Eingehendes links, Ausgehendes rechts. "Antworten" an einer eingehenden
   Nachricht legt eine Freigabe an — gesendet wird erst in der Zentrale. */

const KANAL = {
  whatsapp: { label: 'WhatsApp', cls: 'bg-green-600 text-white' },
  instagram: { label: 'Instagram', cls: 'bg-pink-600 text-white' },
  email: { label: 'E-Mail', cls: 'bg-blue-600 text-white' },
  telefon: { label: 'Telefon', cls: 'bg-amber-600 text-white' },
  sms: { label: 'SMS', cls: 'bg-sky-600 text-white' },
  intern: { label: 'Intern', cls: 'bg-slate-500 text-white' },
};
const TYP_ICON = { nachricht: MessageSquare, termin: Calendar, freigabe: CheckSquare, aufgabe: ClipboardList, ereignis: Zap, zahlung: Zap, aktivitaet: ClipboardList, email: Mail };
const FILTER = [['', 'Alles'], ['nachricht,email', 'Nachrichten'], ['termin', 'Termine'], ['zahlung', 'Zahlungen'], ['freigabe,aufgabe', 'Freigaben & Aufgaben']];

function zeit(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function vorStunden(iso) {
  if (!iso) return null;
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  if (isNaN(h)) return null;
  if (h < 1) return 'gerade eben';
  if (h < 48) return `vor ${h} Std.`;
  return `vor ${Math.round(h / 24)} Tagen`;
}

export default function KanalZeitstrahl({ leadId, stand }) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filter, setFilter] = useState('');
  const [antwortAn, setAntwortAn] = useState(null);
  const [antwortText, setAntwortText] = useState('');
  const [sendet, setSendet] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    try { setDaten((await leadsAPI.zeitstrahl(leadId, filter ? { typen: filter } : {})).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Zeitstrahl konnte nicht geladen werden'); }
    finally { setLaden(false); }
  }, [leadId, filter]);
  // `stand`: Die Lead-Seite zählt hoch, wenn sich von außen etwas geändert hat (z. B. Termin verschoben) —
  // dann nur neu laden, nicht neu einhängen: Antwort-Entwurf und Filter bleiben stehen.
  useEffect(() => { lade(); }, [lade, stand]);

  const antworten = async () => {
    if (!antwortAn || !antwortText.trim()) return;
    setSendet(true);
    try {
      const r = await leadsAPI.zeitstrahlAntwort(leadId, antwortAn, antwortText.trim());
      toast.success('Entwurf liegt in der Freigabe-Zentrale');
      setAntwortAn(null); setAntwortText('');
      navigate(`/freigabe`, { state: { freigabe_id: r.data?.freigabe_id } });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Entwurf konnte nicht angelegt werden'); }
    finally { setSendet(false); }
  };

  const kopf = daten?.kopf || {};
  const nf = kopf.nachfass || {};
  const nfPausiert = nf.pausiert_bis && new Date(nf.pausiert_bis) > new Date();
  const nachfassPause = async () => {
    try {
      if (nfPausiert) { await nachfassAPI.fortsetzen(leadId); toast.success('Nachfass läuft wieder'); }
      else {
        const bis = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
        await nachfassAPI.pausieren(leadId, bis); toast.success('Nachfass 7 Tage pausiert');
      }
      lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nachfass konnte nicht geändert werden'); }
  };
  return (
    <div className="space-y-3" data-testid="kanal-zeitstrahl">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {kopf.herkunft?.kanal && (
          <Badge variant="outline" data-testid="herkunft-badge" title={`Kanal ${kopf.herkunft.kanal} · utm_source ${kopf.herkunft.quelle || '–'} · utm_medium ${kopf.herkunft.medium || '–'} · utm_campaign ${kopf.herkunft.kampagne || '–'}`}>
            Herkunft: {kopf.herkunft.kanal}{[kopf.herkunft.quelle, kopf.herkunft.medium, kopf.herkunft.kampagne].filter(Boolean).length ? ` (${[kopf.herkunft.quelle, kopf.herkunft.medium, kopf.herkunft.kampagne].filter(Boolean).join(' · ')})` : ''}{kopf.herkunft.station ? ` · Station ${kopf.herkunft.station}` : ''}
          </Badge>
        )}
        {kopf.stufe && <Badge variant="secondary">Stufe: {kopf.stufe}</Badge>}
        {kopf.letzte_aktivitaet && <span className="text-muted-foreground">Letzte Aktivität {vorStunden(kopf.letzte_aktivitaet)}</span>}
        {nf.aktiv && (
          <button onClick={nachfassPause} data-testid="nachfass-pause"
                  className={`rounded-full px-2 py-0.5 border ${nfPausiert ? 'border-amber-400 text-amber-700' : 'border-border text-muted-foreground'}`}
                  title={nfPausiert ? 'Nachfass fortsetzen' : 'Nachfass 7 Tage pausieren'}>
            {nfPausiert ? `Nachfass pausiert bis ${new Date(nf.pausiert_bis).toLocaleDateString('de-DE')}`
              : nf.letzter_versuch ? `Nachfass ${nf.letzter_versuch.versuch_nr}/${nf.versuche_gesamt} · ${nf.letzter_versuch.kanal}` : 'Nachfass aktiv'}
          </button>
        )}
        {kopf.ampel && <span className={`inline-block h-3 w-3 rounded-full ${{ gruen: 'bg-green-500', gelb: 'bg-amber-400', rot: 'bg-red-500' }[kopf.ampel] || 'bg-slate-400'}`} title="Einschätzung" />}
        <div className="ml-auto flex gap-1">
          {FILTER.map(([wert, label]) => (
            <button key={wert} onClick={() => setFilter(wert)}
                    className={`rounded-full px-2 py-0.5 border ${filter === wert ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>{label}</button>
          ))}
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={lade}><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {laden ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Lädt …</div>
        : !daten?.eintraege?.length ? <p className="text-sm text-muted-foreground text-center py-8">Noch nichts im Zeitstrahl.</p>
        : (
          <div className="space-y-2">
            {daten.eintraege.map((e, i) => {
              const Icon = TYP_ICON[e.typ] || Zap;
              const rechts = e.richtung === 'aus';
              const k = KANAL[e.kanal];
              return (
                <div key={`${e.referenz?.collection}-${e.referenz?.id}-${i}`} className={`flex ${rechts ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg border p-2.5 text-sm ${rechts ? 'bg-primary/5 border-primary/20' : 'bg-card border-border'}`}>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" />
                      {k && <span className={`rounded px-1.5 py-0.5 ${k.cls}`}>{k.label}</span>}
                      <span className="font-medium text-foreground">{e.titel}</span>
                      <span className="ml-auto mono">{zeit(e.zeit)}</span>
                    </div>
                    {e.text && <p className="mt-1 whitespace-pre-wrap">{e.text}</p>}
                    {(e.bearbeiter || e.faellig_am) && (
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {e.bearbeiter ? `von ${e.bearbeiter}` : ''}{e.faellig_am ? ` · fällig ${zeit(e.faellig_am)}` : ''}
                      </p>
                    )}
                    {e.typ === 'nachricht' && e.richtung === 'ein' && e.konversation_id && (
                      antwortAn === e.konversation_id && antwortAn !== null && daten.eintraege[i] === e ? null : (
                        <button className="mt-1 text-[11px] underline flex items-center gap-1" onClick={() => { setAntwortAn(e.konversation_id); setAntwortText(''); }}>
                          <Reply className="h-3 w-3" /> Antworten
                        </button>
                      )
                    )}
                    {e.ereignis_typ === 'feld_autopflege' && e.details && !e.details.rueckgaengig_von && e.details.quelle !== 'rueckgaengig' && (
                      <button className="mt-1 text-[11px] underline" data-testid="autopflege-rueckgaengig" onClick={async () => {
                        try {
                          const r = await autopflegeAPI.rueckgaengig(leadId, e.referenz?.id);
                          toast[r.data.ok ? 'success' : 'error'](r.data.ok ? 'Rückgängig gemacht' : r.data.grund);
                          lade();
                        } catch (err) { toast.error(err?.response?.data?.detail || 'Rückgängig fehlgeschlagen'); }
                      }}>Rückgängig</button>
                    )}
                    {e.typ === 'freigabe' && ['offen', 'zurueckgestellt'].includes(e.status) && (
                      <button className="mt-1 text-[11px] underline" onClick={() => navigate('/freigabe')}>In der Freigabe-Zentrale öffnen</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {antwortAn && (
        <div className="rounded-lg border border-primary/30 p-3 space-y-2">
          <p className="text-xs text-muted-foreground">Antwort formulieren — sie wird nicht sofort gesendet, sondern landet als Entwurf in der Freigabe-Zentrale.</p>
          <textarea value={antwortText} onChange={(e) => setAntwortText(e.target.value)} rows={4}
                    className="w-full text-sm rounded-md border border-border bg-background p-2" placeholder="Deine Antwort …" />
          <div className="flex gap-2">
            <Button size="sm" onClick={antworten} disabled={sendet || !antwortText.trim()}>{sendet ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Als Entwurf in die Freigabe'}</Button>
            <Button size="sm" variant="ghost" onClick={() => setAntwortAn(null)}>Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
